import {
  Caption1,
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Edit16Regular, MoreHorizontal16Regular, PersonSubtract16Regular } from "@fluentui/react-icons";
import { CopyIconButton } from "@/components/copy-icon-button";
import { FuiButton } from "@/components/fui-control";
import { PageHeaderTitle } from "@/components/ui-typography";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { recordWorkspaceAudit } from "@/features/access/workspace-access";
import { DIRECTORY_ROLE_LABEL, DIRECTORY_ROLE_LINE } from "@/features/directory/directory";
import { setDirectoryRole } from "@/features/directory/directory-store";
import type { DirectoryPerson } from "@/features/directory/directory-view";
import { DirectoryRoleIcon } from "@/features/directory/role-glyphs";
import type { MemberScenario } from "./member-analytics";

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  identity: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    flex: "1 1 auto",
  },
  text: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    minWidth: 0,
    flex: "1 1 auto",
  },
  name: {
    margin: 0,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground1,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    overflow: "hidden",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  keep: {
    flexShrink: 0,
  },
  emailText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  roleCopy: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    maxWidth: "240px",
    whiteSpace: "normal",
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
  },
  dangerItem: {
    "&&&": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover:active": { color: tokens.colorPaletteRedForeground1 },
    "&&& .fui-MenuItem__icon": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover .fui-MenuItem__icon": { color: tokens.colorPaletteRedForeground1 },
    "&&&:hover:active .fui-MenuItem__icon": { color: tokens.colorPaletteRedForeground1 },
  },
  menuLabel: {
    padding: "6px 12px 2px",
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
});

interface MemberProfileHeaderProps {
  person: DirectoryPerson;
  canEdit: boolean;
  locked: boolean;
  scenario: MemberScenario;
  onEdit: () => void;
  onRemove: () => void;
  onScenarioChange: (scenario: MemberScenario) => void;
}

export function MemberProfileHeader({
  person,
  canEdit,
  locked,
  scenario,
  onEdit,
  onRemove,
  onScenarioChange,
}: MemberProfileHeaderProps) {
  const styles = useStyles();
  const roleLabel = DIRECTORY_ROLE_LABEL[person.grant.role];
  const metaTitle = [roleLabel, person.email].filter(Boolean).join(" · ");
  const showMenu = !locked || import.meta.env.DEV;

  return (
    <div className={styles.row}>
      <div className={styles.identity}>
        <PersonAvatar name={person.name} size={32} tip={false} />
        <div className={styles.text}>
          <PageHeaderTitle as="h1" size="page" className={styles.name} title={person.name}>
            {person.name}
          </PageHeaderTitle>
          <span className={styles.meta} title={metaTitle}>
            <span className={styles.keep}>{roleLabel}</span>
            {person.email ? (
              <>
                <span className={styles.keep} aria-hidden>
                  ·
                </span>
                <span className={styles.emailText} title={person.email}>
                  {person.email}
                </span>
                <CopyIconButton value={person.email} label="이메일 복사" />
              </>
            ) : null}
          </span>
        </div>
      </div>
      <div className={styles.actions}>
        {canEdit ? (
          <FuiButton appearance="secondary" icon={<Edit16Regular />} onClick={onEdit}>
            프로필 수정
          </FuiButton>
        ) : null}
        {showMenu ? (
          <Menu
            checkedValues={{ role: [person.grant.role], scenario: [scenario] }}
            onCheckedValueChange={(_, data) => {
              const next = data.checkedItems[0];
              if (data.name === "role") {
                if (next !== "admin" && next !== "editor" && next !== "viewer") return;
                if (next === person.grant.role) return;
                if (setDirectoryRole(person.id, next)) {
                  recordWorkspaceAudit("역할 변경", `${person.name}의 역할을 ${DIRECTORY_ROLE_LABEL[next]}로 바꿨습니다.`);
                }
                return;
              }
              if (next === "default" || next === "empty" || next === "heavy" || next === "unbalanced") onScenarioChange(next);
            }}
          >
            <MenuTrigger disableButtonEnhancement>
              <button type="button" className={styles.menuBtn} aria-label={`${person.name} 동작`}>
                <MoreHorizontal16Regular />
              </button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {locked ? null : (
                  <>
                    {(["admin", "editor", "viewer"] as const).map((role) => (
                      <MenuItemRadio key={role} name="role" value={role} icon={<DirectoryRoleIcon role={role} />}>
                        <span className={styles.roleCopy}>
                          <span>{DIRECTORY_ROLE_LABEL[role]}</span>
                          <Caption1 className={styles.quiet}>{DIRECTORY_ROLE_LINE[role]}</Caption1>
                        </span>
                      </MenuItemRadio>
                    ))}
                    <MenuDivider />
                    <MenuItem className={styles.dangerItem} icon={<PersonSubtract16Regular />} onClick={onRemove}>
                      워크스페이스에서 제거
                    </MenuItem>
                  </>
                )}
                {import.meta.env.DEV ? (
                  <>
                    {locked ? null : <MenuDivider />}
                    <div className={styles.menuLabel}>목업</div>
                    <MenuItemRadio name="scenario" value="default">
                      기본 데이터
                    </MenuItemRadio>
                    <MenuItemRadio name="scenario" value="empty">
                      데이터 없음
                    </MenuItemRadio>
                    <MenuItemRadio name="scenario" value="heavy">
                      대량 데이터
                    </MenuItemRadio>
                    <MenuItemRadio name="scenario" value="unbalanced">
                      불규칙 데이터
                    </MenuItemRadio>
                  </>
                ) : null}
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : null}
      </div>
    </div>
  );
}
